export const MAX_MOBILE_WIDTH = '768px'
export const MIN_MOBILE_HEIGHT = '700px'
export const SHORT_HEIGHT = '500px'


// Server-side IAlertType has alertType/alertCode/status fields,
// but the client receives alerts as an array of string IDs
export type IAlertType = string[]

export enum ItemTypesEnum {
    PET = 'pet',
    PERSON = 'person',
}

export interface IItem {
    _id: string
    accountId: string
    ownerId: string
    deviceId: string
    type: ItemTypesEnum
    name: string
    description: string
}

export interface IDeviceStatus {
    safeZoneDate: string
    safeZoneViolation: boolean
    occupiedZone?: string
    sleeping: boolean
    sleepStart: number
    sleepDuration: number
    sleepRepeat: boolean
    wakeReason?: string
    backgroundInterval: number
    lastContact: string
    powerOn: boolean
    chargerStatus: string
    charging: boolean
    batteryAlert: number
    beaconInRange: boolean
    firmwareVersion: string
    batteryPercent: string
    powerSavingsScanRate: number
    noCellularScanRate: number
    mt: number
    ledState: number
    deviceCommandInProgressStatus: string
    alerts: string[]
    locationsSinceBir?: number
}

export interface IDevice {
    _id: string
    accountId: string
    ownerId: string
    petId: string
    itemId?: string
    billInfoId?: string
    mfg?: string
    iccid: string
    mdn?: string
    imsi?: string
    planId?: string
    status: IDeviceStatus
    item?: IItem
    deviceName: string
    lastLocation: IPoint
    lastReport: IPoint
    lastEvent: IEvent
    isActive?: boolean
    createdAt?: string
    updatedAt?: string
}

export interface IGibiLocationCommand {
    createdAt: string
    'featureType': 'gibiPosition'
    'gibiMessageType': string
    'account': string
    'status': number
    'timeStamp': number
    'latitude': number
    'longitude': number
    'altitude': number
    'speed': number
    'heading': number
    'accuracy': number
    'ttf': number
    'battery': string
    'chargerStatus': string
    'chargingStatus': number
    'deviceId': string
    'receivedTime': string
    'rawData': string
    batteryPercent?: string
    itemId?: string
}

export interface IPoint {
    _id: string
    type: 'Feature',
    geometry: {
        type: string,
        coordinates: number[]
    },
    properties: IGibiLocationCommand
}

export interface ICommandRequest {
    cmd: string
}

export interface IDeviceUpdate {
    subType: string;
    deviceStatus: IDeviceStatus
    commandRequest?: ICommandRequest
}

export interface ICommandResponse {
    command: string;
    success: boolean
    errorMessage?: string
}

export interface ISafeZoneEvent {
    outOfZone: boolean
    zoneName?: string
}

export interface IEvent {
    type?: string
    _id: string
    createdAt: string
    eventType: string
    userId: string
    iccid: string
    itemId: string
    deviceUpdate?: IDeviceUpdate
    commandResponse?: ICommandResponse
    safeZoneEvent?: ISafeZoneEvent
    read: boolean
}

export interface IPet {
    _id: string
    accountId: string
    microchip?: string
    iconColor?: string
    colorId: string
    gender: string
    species: string
    breed: string
    petName: string
    birthDate: string
    petImage: string
    imageURL: string
    petWeight: string
    status?: string
    createdAt?: string
    updatedAt?: string
}

export interface DeviceData {
    lastValidPosition: IPoint
    lastTenPositions: IPoint[]
    lastTenEvents: IEvent[]
    unreadEvents: IEvent[]
}

export interface IHomeAddress {
    country: string
    companyName?: string
    address1?: string
    address2?: string
    address3?: string
    province?: string
    line1?: string
    line2?: string
    city?: string
    canadaCity?: string
    postalCode?: string
    streetAddr?: string
    apartment?: string
    usaCity?: string
    state?: string
    zipCode?: string
}

export interface IAccount {
    _id?: string
    owner?: string
    firstName: string
    lastName: string
    email: string
    mobile: string
    smsOptIn: 'Opt-in' | 'Opt-out' | 'pending'
    timeZone: string
    status?: string
    homeAddress: IHomeAddress
    customerId?: string
    country?: string
    useAdvancedFeatures?: boolean
}

export interface MapDataSet {
    pets: IPet[]
    devices: IDevice[]
    safeZones: IZone[]
    setSafeZones: (z: IZone[]) => void
    powerZones: any[]
}

export interface AccountDataSet {
    account: IAccount
    billing: IBillingInfo[]
    contacts: IContact[]
    devicePlans: ISubscription[]
    paymentMethod: string
}

export interface ApplicationDataSet {
    species: any[]
    breeds: any[]
    timeZones: any[]
    iconColors: any[]
    rates: any[]
    alertTypes: any[]
    plans: any[]
    codes: any[]
}

export interface RegistrationDataSet {
    timeZones: any[]
    plans: any[]
    species: any[]
    breeds: any[]
    iconColors: any[]
}

export interface IStripePlan {
    accountId: string
    createdAt: string
    description: string
    deviceId: string
    planAmount: string
    planEnd: string
    planStart: string
    planType: string
    status: string
    stripePlanID: string
    stripePriceID: string
    updatedAt: string
}

export interface EventsDataState {
    events: any[]
    unreadCount: number
}

export interface IUsaAddress {
    streetAddr: string
    apartment: string
    city: string
    state: string
    zipCode: string
}

export interface ICanadaAddress {
    address1: string
    address2: string
    address3: string
    province: string
    city: string
    postalCode: string
}

export interface IBillingInfo {
    firstName: string
    lastName: string
    country: string
    address: IUsaAddress | ICanadaAddress
}

export interface ISubscription {
    planAmount: string
    interval: 'month' | 'year'
    id: string
    deviceName: string
    planType: string
    description: string
    planStart: string
    planEnd: string
    planStatus: string
    billingInfo: IBillingInfo
    deviceId: string
    subscription: string // stripe id
    _id: string
}

export const gibiEmailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+(\|\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+)*$/
export const userEmailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export const usPhoneRegex = /^(\+0?1\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/

export interface IPromoCode {
    code: string
    id: string
    name: string
}

export type ZonePath = {lat: number, lng: number}[]

export interface IZone {
    _id: string
    ownerId?: string
    zoneName: string
    zoneType: 'safe' | 'power'
    zoneCoordinates: ZonePath
    status?: string
}

export interface IContact {
    _id: any
    owner: string
    firstName: string
    lastName: string
    email?: string
    mobileNo: string
    petList: any[]
    smsOptIn: 'Opt-in' | 'Opt-out' | 'pending'
    status?: string
}
